import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { LoadingOutlined } from "@ant-design/icons";
import { Button, Result, Spin } from "antd";
import { setUser } from "store/features/auth/authSlice";

import { exchangeSSO } from "api/ssoService";

import { IUser } from "models/User";

const SSOCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const processCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const returnUrl = params.get("returnUrl");
        const errorParam = params.get("error");

        // Check for error in URL parameters
        if (errorParam) {
          setError(decodeURIComponent(errorParam));
          setProcessing(false);
          return;
        }

        // Check if we have the session code
        if (code) {
          try {
            // Exchange the session code for tokens
            // Note: Backend sets authentication cookies via withCredentials: true
            const exchangeResponse = await exchangeSSO(code);

            // Create user object compatible with existing auth system
            const user: IUser = {
              id: exchangeResponse.id,
              email: exchangeResponse.email,
              firstName: exchangeResponse.firstName,
              lastName: exchangeResponse.lastName,
              jwtToken: exchangeResponse.jwtToken,
              organizationId: exchangeResponse.organization.id,
              username: exchangeResponse.username,
              role: exchangeResponse.role,
              modules: "",
              status: exchangeResponse.status,
              errorMessage: exchangeResponse.errorMessage,
              jobTitle: exchangeResponse.jobTitle,
              hasProfilePicture: !!exchangeResponse.profilePictureImageSrc,
              profilePictureImageSrc: exchangeResponse.profilePictureImageSrc,
              requiresMfa: false,
              // System-admin flag; UserReducer derives isTuringAdmin from it.
              canImpersonate: exchangeResponse.canImpersonate,
              phoneNumber: exchangeResponse.phoneNumber,
              dataAccess: exchangeResponse.dataAccess,
              dataSources: exchangeResponse.dataSources,
              organization: {
                id: exchangeResponse.organization.id,
                name: exchangeResponse.organization.name,
                emailDomain: exchangeResponse.organization.emailDomain,
                postalCode: exchangeResponse.organization.postalCode,
                city: exchangeResponse.organization.city,
                provinceState: exchangeResponse.organization.provinceState,
                address: exchangeResponse.organization.address,
                phoneNumber: exchangeResponse.organization.phoneNumber,
                requireMfaForOrganization:
                  exchangeResponse.organization.requireMfaForOrganization,
                organizationAccountType:
                  exchangeResponse.organization.organizationAccountType,
                allowUserDefinedFields:
                  exchangeResponse.organization.allowUserDefinedFields,
                primaryContact: exchangeResponse.organization.primaryContact,
                maximumUsers: exchangeResponse.organization.maximumUsers,
                betaFeatures: exchangeResponse.organization.betaFeatures,
                authenticationMethods: exchangeResponse.organization.authenticationMethods
              }
            };

            // Update Redux auth state
            dispatch(setUser(user));

            // Navigate to return URL or dashboard
            const finalReturnUrl = returnUrl || "/app";
            history.push(finalReturnUrl);
            return;
          } catch (exchangeError) {
            setError("Failed to complete SSO authentication. Please try again.");
            setProcessing(false);
            return;
          }
        }

        // No code found - invalid callback
        setError(
          "Invalid SSO callback - no session code found. Please try logging in again."
        );
      } catch (callbackError) {
        setError("Failed to process authentication response. Please try again.");
      } finally {
        setProcessing(false);
      }
    };

    // Add a small delay to ensure all data is available
    const timer = setTimeout(processCallback, 500);

    return () => clearTimeout(timer);
  }, [location.search, dispatch, history]);

  const handleReturnToLogin = () => {
    history.push("/sso");
  };

  const handleRetry = () => {
    setError(null);
    setProcessing(true);
    // Trigger processing again
    window.location.reload();
  };

  if (processing) {
    return (
      <div>
        <Result
          status="info"
          title="Completing sign in..."
          subTitle="Please wait while we process your authentication."
          extra={[
            <>
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                size="large"
              />
            </>
          ]}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Authentication Error"
        subTitle={error}
        extra={[
          <Button type="primary" key="retry" onClick={handleRetry}>
            Try Again
          </Button>,
          <Button key="login" onClick={handleReturnToLogin}>
            Return to Login
          </Button>
        ]}
      />
    );
  }

  // This should not be reached as loginWithSSO redirects
  return (
    <Result
      status="success"
      title="Authentication Successful"
      subTitle="You will be redirected shortly..."
      extra={
        <Button type="primary" onClick={() => history.push("/app")}>
          Continue to Dashboard
        </Button>
      }
    />
  );
};

export default SSOCallback;
